import { alpha, createTheme, type PaletteMode } from "@mui/material/styles";

export function createAppTheme(mode: PaletteMode) {
  const dark = mode === "dark";
  const primary = dark ? "#9cb7ff" : "#315cba";
  const secondary = dark ? "#5eead4" : "#0f766e";
  const surface = dark ? "#181c23" : "#ffffff";
  const subtle = dark ? "#20252e" : "#f0f3f7";
  const border = dark ? "#303743" : "#dfe4eb";
  const text = dark ? "#f4f7fb" : "#192231";
  const muted = dark ? "#aab4c4" : "#5f6b7d";

  return createTheme({
    palette: {
      mode,
      primary: { main: primary, light: dark ? "#bdd0ff" : "#5279cf", dark: dark ? "#7898e8" : "#244894", contrastText: dark ? "#111720" : "#ffffff" },
      secondary: { main: secondary, contrastText: dark ? "#0f1f1d" : "#ffffff" },
      error: { main: dark ? "#ff8994" : "#c93645" },
      warning: { main: dark ? "#f4c46d" : "#9a5d0b" },
      success: { main: dark ? "#61d6a5" : "#167452" },
      info: { main: dark ? "#83b8ff" : "#2b6fb7" },
      background: { default: dark ? "#10141a" : "#f5f7fa", paper: surface },
      text: { primary: text, secondary: muted },
      divider: border
    },
    shape: { borderRadius: 8 },
    typography: {
      fontFamily: '"Segoe UI Variable Text", "HarmonyOS Sans SC", "Microsoft YaHei UI", "PingFang SC", system-ui, sans-serif',
      h1: { fontSize: 36, lineHeight: 1.2, fontWeight: 760, letterSpacing: 0 },
      h2: { fontSize: 26, lineHeight: 1.28, fontWeight: 740, letterSpacing: 0 },
      h3: { fontSize: 20, lineHeight: 1.35, fontWeight: 700, letterSpacing: 0 },
      subtitle1: { fontSize: 16, lineHeight: 1.5, fontWeight: 650, letterSpacing: 0 },
      body1: { fontSize: 15, lineHeight: 1.7, letterSpacing: 0 },
      body2: { fontSize: 14, lineHeight: 1.65, letterSpacing: 0 },
      button: { fontSize: 14, fontWeight: 680, letterSpacing: 0, textTransform: "none" }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          html: { colorScheme: mode, scrollBehavior: "smooth" },
          body: { letterSpacing: 0, backgroundImage: "none" },
          "::selection": {
            backgroundColor: dark ? "#315cba" : alpha(primary, .2),
            color: dark ? "#ffffff" : text
          }
        }
      },
      MuiAvatar: {
        styleOverrides: { root: { border: `1px solid ${border}`, backgroundColor: dark ? "#23304a" : "#edf2ff", color: dark ? "#bdd0ff" : "#315cba", fontSize: 14, fontWeight: 750 } }
      },
      MuiBadge: {
        styleOverrides: { badge: { minWidth: 18, height: 18, padding: "0 5px", border: `2px solid ${surface}`, backgroundColor: primary, color: "#fff", fontSize: 10, fontWeight: 800 } }
      },
      MuiIconButton: {
        styleOverrides: {
          root: {
            width: 44,
            height: 44,
            borderRadius: 8,
            color: muted,
            transition: "background-color 160ms ease, color 160ms ease, transform 120ms ease",
            "&:hover": { backgroundColor: subtle, color: text },
            "&:active": { transform: "scale(.96)" },
            "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 2 }
          }
        }
      },
      MuiButton: {
        defaultProps: { disableElevation: true },
        styleOverrides: {
          root: {
            minHeight: 44,
            paddingInline: 18,
            gap: 8,
            borderRadius: 8,
            fontSize: 14,
            letterSpacing: 0,
            textTransform: "none",
            transition: "background-color 160ms ease, border-color 160ms ease, color 160ms ease, box-shadow 180ms ease, transform 120ms ease",
            "& svg": { width: 18, height: 18 },
            "&:active": { transform: "scale(.98)" },
            "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: 2 },
            "&.MuiButton-containedPrimary": { boxShadow: dark ? "none" : `0 1px 2px ${alpha("#192231", .12)}, 0 6px 16px ${alpha(primary, .14)}` }
          },
          outlined: { borderColor: dark ? "#3a3e4b" : "#d9dce6", backgroundColor: surface },
          sizeSmall: { minHeight: 44, paddingInline: 13, fontSize: 13 }
        }
      },
      MuiTextField: { defaultProps: { fullWidth: true, size: "small", variant: "outlined" } },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            minHeight: 50,
            borderRadius: 8,
            backgroundColor: surface,
            transition: "background-color 160ms ease, box-shadow 160ms ease",
            "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: dark ? "#555b6b" : "#aeb4c2" },
            "&.Mui-focused": { boxShadow: `0 0 0 3px ${alpha(primary, .14)}` },
            "&.MuiInputBase-multiline": { padding: 14 }
          },
          notchedOutline: { borderColor: dark ? "#3a3e4b" : "#d8dce6" },
          input: { padding: "13px 14px", fontSize: 14, lineHeight: 1.5, "&.MuiInputBase-inputMultiline": { padding: 0 } }
        }
      },
      MuiInputLabel: { styleOverrides: { root: { fontSize: 14, letterSpacing: 0 } } },
      MuiFormHelperText: { styleOverrides: { root: { marginInline: 2, fontSize: 12, lineHeight: 1.5 } } },
      MuiFormControlLabel: { styleOverrides: { label: { fontSize: 13 } } },
      MuiCheckbox: { styleOverrides: { root: { borderRadius: 6 } } },
      MuiSwitch: { styleOverrides: { switchBase: { "&.Mui-checked": { color: secondary, "& + .MuiSwitch-track": { backgroundColor: secondary } } } } },
      MuiToggleButton: {
        styleOverrides: {
          root: {
            minHeight: 44,
            borderColor: border,
            color: muted,
            fontWeight: 700,
            letterSpacing: 0,
            textTransform: "none",
            "&:hover": { backgroundColor: subtle, color: text },
            "&.Mui-selected, &.Mui-selected:hover": { backgroundColor: alpha(primary, dark ? .18 : .09), color: primary },
            "&:focus-visible": { zIndex: 1, outline: `2px solid ${primary}`, outlineOffset: 2 }
          }
        }
      },
      MuiTab: { styleOverrides: { root: { minHeight: 46, paddingInline: 18, fontSize: 14, fontWeight: 720, letterSpacing: 0, textTransform: "none" } } },
      MuiTabs: { styleOverrides: { indicator: { height: 3, borderRadius: "3px 3px 0 0" } } },
      MuiMenu: {
        defaultProps: { elevation: 0 },
        styleOverrides: { paper: { minWidth: 224, marginTop: 8, padding: 6, border: `1px solid ${border}`, borderRadius: 8, backgroundImage: "none", boxShadow: dark ? "0 22px 64px rgba(0,0,0,.48)" : "0 22px 64px rgba(29,36,51,.16)" }, list: { paddingBlock: 0 } }
      },
      MuiMenuItem: { styleOverrides: { root: { minHeight: 42, paddingInline: 11, gap: 10, borderRadius: 7, fontSize: 13, "& svg": { width: 17, height: 17, color: muted }, "&.is-danger, &.is-danger svg": { color: dark ? "#ff858d" : "#d64552" } } } },
      MuiListItemButton: { styleOverrides: { root: { minHeight: 46, borderRadius: 8 } } },
      MuiListSubheader: { styleOverrides: { root: { padding: "8px 10px 6px", backgroundColor: "transparent", lineHeight: 1.5, "& strong, & small": { display: "block" }, "& strong": { fontSize: 14 }, "& small": { marginTop: 2, color: muted, fontSize: 11, fontWeight: 500 } } } },
      MuiTooltip: { styleOverrides: { tooltip: { border: `1px solid ${border}`, borderRadius: 6, backgroundColor: dark ? "#24262e" : "#ffffff", boxShadow: dark ? "0 10px 30px rgba(0,0,0,.26)" : "0 10px 30px rgba(29,36,51,.12)", color: text, fontSize: 12 } } },
      MuiDialog: { styleOverrides: { backdrop: { backgroundColor: dark ? "rgba(0,0,0,.68)" : "rgba(29,36,51,.34)", backdropFilter: "blur(8px)" }, paper: { maxWidth: "none", margin: 16, overflow: "visible", borderRadius: 8, backgroundColor: "transparent", backgroundImage: "none", boxShadow: "none" } } },
      MuiDialogContent: { styleOverrides: { root: { position: "relative", width: "min(500px, calc(100vw - 32px))", maxHeight: "calc(100dvh - 32px)", padding: 26, overflow: "auto", border: `1px solid ${border}`, borderRadius: 8, backgroundColor: surface, boxShadow: dark ? "0 28px 80px rgba(0,0,0,.58)" : "0 28px 80px rgba(29,36,51,.22)" } } },
      MuiDialogTitle: { styleOverrides: { root: { padding: 0, fontSize: 22, fontWeight: 760, letterSpacing: 0 } } },
      MuiDialogContentText: { styleOverrides: { root: { marginTop: 6, color: muted, fontSize: 14, lineHeight: 1.65 } } },
      MuiDialogActions: { styleOverrides: { root: { padding: "18px 0 0", gap: 8 } } },
      MuiPaper: { styleOverrides: { rounded: { borderRadius: 8 } } },
      MuiCard: { defaultProps: { elevation: 0, variant: "outlined" }, styleOverrides: { root: { borderColor: border, borderRadius: 8, backgroundImage: "none" } } },
      MuiCardActionArea: { styleOverrides: { root: { borderRadius: 8, "&:focus-visible": { outline: `2px solid ${primary}`, outlineOffset: -2 } } } },
      MuiChip: { styleOverrides: { root: { height: 30, maxWidth: "100%", borderRadius: 7, fontSize: 12, fontWeight: 700 }, label: { overflow: "hidden", textOverflow: "ellipsis" }, icon: { width: 15, height: 15 } } },
      MuiAlert: { defaultProps: { variant: "outlined" }, styleOverrides: { root: { borderRadius: 8, alignItems: "center", fontSize: 13 }, icon: { paddingBlock: 4 }, message: { paddingBlock: 4 } } },
      MuiSkeleton: { defaultProps: { animation: "wave" }, styleOverrides: { root: { borderRadius: 7 } } },
      MuiTableCell: { styleOverrides: { root: { padding: "13px 16px", borderColor: border, fontSize: 13 }, head: { backgroundColor: subtle, color: muted, fontWeight: 750 } } },
      MuiTableRow: { styleOverrides: { root: { "&:last-child td": { borderBottom: 0 }, "&:hover td": { backgroundColor: dark ? alpha("#ffffff", .025) : alpha("#1d2433", .018) } } } }
    }
  });
}
